/**
 * Pilot Prompt — third widget surface joining `site-agent` and `engage`.
 *
 * A ~320×auto bottom-right card driven by Personalize Show/Ask/Offer
 * acts when policy targets `surface: 'prompt'`. Avatar + name + 1-2
 * line contextual greeting + optional 1-2 quick-reply chips + inline
 * reply pill. Used for email-landing greetings, UTM-aware suggestions,
 * returning-warm-visitor recognition.
 *
 * Tap reply pill (with text) OR any chip → fires `onSubmit(value)`.
 * The bootstrap wires that to "open the chat panel + send the value
 * as the first user message", giving the morph-into-chat behavior
 * buyers now expect from a proactive prompt.
 *
 * Standalone-by-design — does NOT integrate into the per-view state
 * machine in `state.ts`. Lives as an orthogonal overlay, mounted /
 * unmounted by `mountPilotPrompt()` below. Skip-if-engage-open and
 * sticky-per-session rules are enforced by the mount helper, not the
 * component, so the component stays dumb-render.
 *
 * See `docs/archive/personalize-v1-prd.md` "New widget surfaces" section.
 */

import { signal } from '@preact/signals';
import { deviceSig } from '../viewport';
import { mountPreactIsland } from '../voice/preact-mount';

export interface PilotPromptPayload {
  /** Promotion key for sticky-per-session dedupe (sha-256 of policyId+slug). */
  key: string;
  agentName: string;
  /** Avatar URL — falls back to initial-based SVG inside the component. */
  avatarUrl?: string | null;
  /** Optional marketing image (Offer, Slice O2) — rendered as a hero at the
   *  top of the card. Null/absent = conversational (avatar-led) layout. */
  imageUrl?: string | null;
  /** Optional bold headline above the body (Offer, Slice O2). */
  headline?: string | null;
  /** M3/P7 — render the avatar slot as an avatar-stage teaser dock. */
  withTeaserDock?: boolean;
  /** Greeting copy. Plain text, no markdown for v1. 1-3 short sentences. */
  message: string;
  /** Optional quick-reply chips. Max 2 enforced visually. */
  chips?: string[];
  /** Optional primary CTA button (Offer, Slice O1). The bound action is
   *  dispatched by the host via `onCta`. Null/absent = no button. */
  cta?: { label: string } | null;
  /** Optional theme override; falls back to root --sp-primary. */
  primaryColor?: string;
  /** Where the prompt appears. Default `bottom-right`. */
  position?: 'bottom-right' | 'top-right';
}

export interface PilotPromptHandlers {
  /** Visitor sent a reply — value is the chip text OR free-typed input. */
  onSubmit(value: string): void;
  /** Visitor clicked the primary CTA button (Offer action). */
  onCta?(): void;
  /** Visitor dismissed without engaging. */
  onDismiss(): void;
}

function initialAvatar(name: string): string {
  const initial = (name?.slice(0, 1) || 'A').toUpperCase();
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40"><rect width="40" height="40" fill="#e5e7ea"/><text x="50%" y="56%" text-anchor="middle" font-family="-apple-system,sans-serif" font-size="16" font-weight="600" fill="#646a79">${initial}</text></svg>`;
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
}

function PilotPromptCard({
  payload,
  handlers,
  inputValue,
}: {
  payload: PilotPromptPayload;
  handlers: PilotPromptHandlers;
  inputValue: ReturnType<typeof signal<string>>;
}) {
  const avatar = payload.avatarUrl || initialAvatar(payload.agentName);
  const chips = (payload.chips ?? []).slice(0, 2);

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    handlers.onSubmit(trimmed);
  };

  return (
    <div
      class="sp-pilot-prompt-card"
      style={
        payload.primaryColor
          ? ({ ['--sp-pilot-prompt-primary' as string]: payload.primaryColor } as Record<string, string>)
          : undefined
      }
      data-position={payload.position ?? 'bottom-right'}
    >
      <button
        class="sp-pilot-prompt-close"
        type="button"
        aria-label="Dismiss"
        onClick={() => handlers.onDismiss()}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
          <path
            d="M18 6L6 18M6 6L18 18"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      {payload.withTeaserDock && !payload.imageUrl && (
        /* D3 — the persona face IS the media block when the offer has no
           image of its own (mockup's gradient hero). Carries the dock
           class so the stage re-parents here. */
        <div class="sp-pilot-prompt-hero-dock sp-pilot-prompt-avatar-dock" />
      )}
      {payload.imageUrl && (
        <img
          class="sp-pilot-prompt-hero"
          src={payload.imageUrl}
          alt=""
          style={{
            width: '100%',
            height: '128px',
            objectFit: 'cover',
            borderRadius: '10px',
            marginBottom: '10px',
            display: 'block',
          }}
        />
      )}

      <div class="sp-pilot-prompt-header">
        {payload.withTeaserDock && payload.imageUrl ? (
          /* P7 — teaser dock in the circle (an offer image owns the hero). */
          <span class="sp-pilot-prompt-avatar sp-pilot-prompt-avatar-dock" />
        ) : (
          <img class="sp-pilot-prompt-avatar" src={avatar} alt={payload.agentName} />
        )}
        <span class="sp-pilot-prompt-name">{payload.agentName}</span>
      </div>

      {payload.headline && (
        <div
          class="sp-pilot-prompt-headline"
          style={{ fontWeight: 700, fontSize: '15px', marginTop: '8px', lineHeight: 1.3 }}
        >
          {payload.headline}
        </div>
      )}

      <div class="sp-pilot-prompt-message">{payload.message}</div>

      {payload.cta?.label && (
        <button
          class="sp-pilot-prompt-cta"
          type="button"
          onClick={() => handlers.onCta?.()}
          style={{
            background: payload.primaryColor || 'var(--sp-pilot-prompt-primary, #2563eb)',
            color: '#fff',
            border: 'none',
            borderRadius: '9999px',
            padding: '8px 16px',
            marginTop: '10px',
            fontSize: '13px',
            fontWeight: 600,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          {payload.cta.label}
        </button>
      )}

      {chips.length > 0 && (
        <div class="sp-pilot-prompt-chips">
          {chips.map((chip) => (
            <button
              key={chip}
              class="sp-pilot-prompt-chip"
              type="button"
              onClick={() => submit(chip)}
            >
              {chip}
            </button>
          ))}
        </div>
      )}

      <form
        class="sp-pilot-prompt-reply"
        onSubmit={(e) => {
          e.preventDefault();
          submit(inputValue.value ?? '');
        }}
      >
        <input
          class="sp-pilot-prompt-input"
          type="text"
          placeholder="Reply…"
          value={inputValue.value}
          onInput={(e) => {
            inputValue.value = (e.currentTarget as HTMLInputElement).value;
          }}
          autocomplete="off"
        />
        <button class="sp-pilot-prompt-send" type="submit" aria-label="Send">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="m5 12 7-7 7 7M12 19V5"
              stroke="currentColor"
              stroke-width="2.4"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </form>
    </div>
  );
}

// ============================================================
// Public mount API — used by the widget bootstrap (and dev console)
// ============================================================

const SESSION_DISMISSED_KEY = 'sp_pilot_prompt_dismissed';

function readDismissedKeys(): Set<string> {
  try {
    const raw = sessionStorage.getItem(SESSION_DISMISSED_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return new Set();
    return new Set(arr.filter((x): x is string => typeof x === 'string'));
  } catch {
    return new Set();
  }
}

function recordDismissed(key: string): void {
  const set = readDismissedKeys();
  set.add(key);
  try {
    sessionStorage.setItem(SESSION_DISMISSED_KEY, JSON.stringify([...set]));
  } catch {
    // sessionStorage may be unavailable (Safari private mode) — fail open.
  }
}

export interface PilotPromptController {
  /** Tear down DOM + unmount Preact tree. Idempotent. */
  close(): void;
  /** True once `close()` has been called. */
  closed: boolean;
}

export interface MountPilotPromptOptions {
  /** Where to anchor the card. Defaults to `document.body`. */
  parent?: HTMLElement;
  /** Override sticky-dedupe — useful for dev/manual reopens. */
  forceShow?: boolean;
  /**
   * M3 (mobile UX program, behind ui.mobileV2): render as the unified
   * Card/teaser — device-aware (phone = full-width teaser, desktop =
   * corner card) via a data-device stamp + variant class. Also enforces
   * the per-session pacing budget (P9).
   */
  mobileV2?: boolean;
  /** CSS object-position for the teaser-dock greeter crop (Phase 0 contract). */
  greeterFocalPoint?: string;
  /**
   * M3/P7 — when provided (with mobileV2), the card docks the persistent
   * greeter video (avatar-stage teaser dock) instead of the static
   * avatar image, and releases it on close.
   */
  teaserDock?: { register(anchor: HTMLElement): void; release(): void };
}

// ---- P9: per-session proactive pacing budget (mobile UX program) ----
// A visitor sees at most ~2 proactive surfaces per session unless they
// engage; an accept clears the counter (etiquette is earned back).
// Interstitials will count as 2 when Phase 5 lands (O5).
const SESSION_BUDGET_KEY = 'sp_proactive_shown';
export const PROACTIVE_SESSION_BUDGET = 2;

export function readProactiveShownCount(): number {
  try {
    return Number(sessionStorage.getItem(SESSION_BUDGET_KEY) ?? '0') || 0;
  } catch {
    return 0;
  }
}
export function recordProactiveShown(weight = 1): void {
  try {
    sessionStorage.setItem(SESSION_BUDGET_KEY, String(readProactiveShownCount() + weight));
  } catch {
    /* storage unavailable — fail open */
  }
}
export function clearProactiveBudget(): void {
  try {
    sessionStorage.removeItem(SESSION_BUDGET_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Mount the Pilot Prompt as a fixed-position card. Returns a
 * controller for programmatic close. Wires session-sticky dismiss
 * (same `payload.key` won't reshow until session ends).
 */
export function mountPilotPrompt(
  initialPayload: PilotPromptPayload,
  handlers: PilotPromptHandlers,
  options: MountPilotPromptOptions = {},
): PilotPromptController | null {
  let payload = initialPayload;
  if (!options.forceShow && readDismissedKeys().has(payload.key)) {
    return null;
  }
  // P9 — pacing budget: cap proactive surfaces per session. Dismissal
  // stickiness alone lets a visitor be re-prompted under fresh decision
  // keys; the budget stops that. forceShow (QA hook) bypasses.
  // V3-D: the budget is UNIVERSAL — desktop cards used to bypass it
  // (mobileV2-gated), stacking on top of arrival offers.
  if (!options.forceShow && readProactiveShownCount() >= PROACTIVE_SESSION_BUDGET) {
    return null;
  }

  const parent = options.parent ?? document.body;
  const anchor = document.createElement('div');
  anchor.className =
    'sp-pilot-prompt-anchor' + (options.mobileV2 ? ' sp-pilot-prompt-anchor--mv2' : '');
  if (options.mobileV2) {
    anchor.setAttribute('data-device', deviceSig.value);
    if (options.greeterFocalPoint) {
      anchor.style.setProperty('--sp-greeter-focus', options.greeterFocalPoint);
    }
  }
  parent.appendChild(anchor);
  if (!options.forceShow) recordProactiveShown();
  // P7 — dock the persistent greeter video into the card's avatar slot.
  if (options.mobileV2 && options.teaserDock) {
    payload = { ...payload, withTeaserDock: true };
    // Register after mount so the Preact tree has rendered the slot; the
    // stage's deferred-dock handles anchor-not-yet-present.
    queueMicrotask(() => {
      const slot = anchor.querySelector<HTMLElement>('.sp-pilot-prompt-avatar-dock');
      if (slot) options.teaserDock!.register(slot);
    });
  }

  const inputValue = signal('');
  let closed = false;

  const close = () => {
    if (closed) return;
    closed = true;
    if (options.mobileV2 && options.teaserDock) options.teaserDock.release();
    try {
      teardown();
    } finally {
      if (anchor.parentElement) anchor.parentElement.removeChild(anchor);
    }
  };

  const wrappedHandlers: PilotPromptHandlers = {
    onSubmit(value) {
      handlers.onSubmit(value);
      // Tapping a chip / sending a reply implicitly accepts — no need
      // to record dismissed (next /decide round will pick the next act).
      // P9: engagement earns the budget back.
      if (options.mobileV2) clearProactiveBudget();
      close();
    },
    onDismiss() {
      recordDismissed(payload.key);
      handlers.onDismiss();
      close();
    },
  };

  const teardown = mountPreactIsland(
    anchor,
    <PilotPromptCard payload={payload} handlers={wrappedHandlers} inputValue={inputValue} />,
  );

  return {
    close,
    get closed() {
      return closed;
    },
  };
}
